import { RootState } from '../../store/store';
import { PageConfig } from '../../store/features/pageConfig';
import { SiteType } from '../../store/features/siteType';
import { SiteOptions } from '../../store/features/options';
import { IndustryHeader } from '../../components/hub/brandHub/store/features/industryHeader';
import { UniqueIdentifier } from '../../types';

export type GenerateLeadType = {
  event_label: string;
  form_id: string;
  form_title: string;
  contentType?: string;
};

export type CTAClickType = {
  [key: string]: string;
};

export enum GA4EventNames {
  USER_SEGMENT_SET = 'user_segment_set',
  USER_LOGGED_IN_SET = 'user_logged_in_set',
  USER_ID_SET = 'user_id_set',
  CONTENT_TYPE_SET = 'content_type_set',
  CONTENT_GATED = 'content_gated_set',
  ARTICLE_SET = 'article_portfolio_set',
  ANALYTICS_ID_SET = 'analytics_id_set',
  HUB_OPTIONS_SET = 'hub_options_set',
  DIVISION_OPTIONS_SET = 'division_options_set',
  HUB_PRODUCT_CODE_SET = 'hub_product_code_set',
  FORM_TYPE_SET = 'form_type_set',
  CHECKOUT_PROGRESS = 'checkout_progress',
  PAYMENT_TYPE = 'payment_type',
  SESSION_SET = 'session_set',
  NAVBAR_CLICK = 'navbar_click',
  CTA_CLICK = 'cta_click',
  DOWNLOAD_CLICK = 'download_click',
  VIDEO_PLAY_CLICK = 'video_play_click',
  BUY_BUTTON_CLICK = 'buy_button_click',
  FILTER_CHANGE = 'filter_change',
  PURCHASE = 'purchase',
  // https://itmebusiness.atlassian.net/browse/KT-2837
  SIGN_UP = 'sign_up',
  LOGIN = 'login',
  LOGOUT = 'logout',
  GENERATE_LEAD = 'generate_lead',
}

export enum GA4EventCategories {
  USER = 'User Tracking',
  CONTENT = 'Content Context',
  ECOMMERCE = 'Ecommerce',
  LEADGEN = 'Lead Generation',
  AGENDA = 'Agenda',
  SITE_INTERACTION = 'Site Interaction',
  CUSTOM_MARKETING_INTERACTION = 'Custom Marketing Interaction',
}

export type DataLayerValue =
  | string
  | number
  | boolean
  | Record<string, any>
  | Array<Record<string, any> | string | number | boolean>;

export enum LegacyEventKeys {
  EVENT = 'event',
  GA = 'ga',
  ECOMMERCE = 'ecommerce',
  PURCHASE = 'Purchase',
}

export enum VariablesEventKeys {
  SEGMENTS = 'segments',
  KNNEVENT = 'knnevent',
  KNNEVENT_ANALYTICS = 'knnevent.analytics',
  L1_TOPICS = 'l1Topics',
  L2_TOPICS = 'l2Topics',
  DIVISION_ID = 'division.id',
  DIVISION_NAME = 'division.name',
  DIVISION_INFRONT_BUSINESS_UNIT = 'division.infrontBusinessUnit',
  MAIN_TRACKING_TOPIC = 'mainTrackingTopic',
  KNNEVENT_NAME = 'knnevent.name',
  KNNEVENT_EVENT_ID = 'knnevent.eventId',
  KNNEVENT_EVENT_CODE = 'knnevent.eventCode',
  KNNEVENT_DAYS_TO_EVENT_END = 'knnevent.daysToEventEnd',
  GATED_CONTENT = 'gatedContent',
  USER_LOGGED_IN = 'userLoggedIn',
  USER_ID = 'userID',
  BRAND = 'brand',
  FORMTYPE = 'formType',
  CONTENT_TYPE = 'contentType',
  MAIN_PORTFOLIO = 'mainPortfolio',
  KNNHUB = 'knnhub',
  KNNARTICLE = 'knnarticle',
  LINKED_BRANDS = 'linkedBrands',
  DIVISION_OPTION_ID = 'divisionOption.id',
  DIVISION_OPTION_NAME = 'divisionOption.name',
  DIVISION_OPTION_INFRONT_BUSINESS_UNIT = 'divisionOption.infrontBusinessUnit',
}

export type LegacyEventKey = [LegacyEventKeys.EVENT, LegacyEventKeys.GA];
export type PushKey =
  | VariablesEventKeys
  | LegacyEventKey
  | [LegacyEventKeys.ECOMMERCE]
  | [LegacyEventKeys.PURCHASE];
export type PushValue = DataLayerValue | DataLayerValue[];

export interface GA4BaseContext {
  [key: string]: any;
}

export interface GA4EventBase {
  event: GA4EventNames;
  event_category: GA4EventCategories;
  event_label: string;
  form_id?: string;
  form_title?: string;
  payment_type?: string;
  [key: string]: any;
}

export enum EventCategoryGA {
  BUY = 'Buy',
  NAVBAR = 'Navbar',
  REGISTRATION = 'Registration',
  LOGIN = 'Login',
  SESSION = 'Session',
}

export enum GA4EventLabels {
  USER_SEGMENTS_FROM_MA_CONFIG = 'User Segments from MA config or cookie',
  KNECT365_NAVIGATION_LINK_CLICKED = 'Knect365 Navigation link clicked: {{path}}',
  FORM_TYPE_SET = 'Form type set: {{value}} - {{label}}',
  BUY_BUTTON_CLICKED = 'Buy button clicked - {{counter}} package(s)',
  FILTER_CHANGED = 'Filter changed: {{category}} = {{value}}',
  CHECKOUT_DETAILS = 'Checkout details {{step}}',
  PRODUCT_DETAILS = 'Product details',
  SESSION_DETAILS = 'Session details',
  PURCHASE_DETAILS = 'Purchase details',
  GATED_CONTENT_ACCESS_REQUIRED = 'Gated content access required',
  USER_LOGIN_STATE_DETECTED = 'User login state detected on page load',
  USER_ID_RETRIEVED_FROM_COGNITO = 'User ID retrieved from Cognito',
  GOOGLE_ANALYTICS_ID_FOR_SITE = 'Google Analytics ID (UA) for {{siteType}}',
  HUB_PRODUCT_CODE_DETAILS = 'Hub product code details',
  HUB_OPTIONS_DETAILS = 'Hub options details',
  ASSOCIATED_PORTFOLIO_WITH_ARTICLE = 'Associated portfolio with article',
  DIVISION_DETAILS = 'Division details',
  CTA_CLICK = 'CTA {{cta_title}} clicked in {{module_type}} module',
  CTA_DOWNLOAD = 'Download CTA {{cta_title}} clicked',
  VIDEO_PLAY = 'Video {{video_title}} play button clicked',
  PAYMENT_TYPE = 'Selected Payment Type - {{payment_option}}',
}

export interface ReduxStore extends RootState {
  pageConfig: PageConfig;
  siteType: {
    data: SiteType;
  };
  options: {
    data: SiteOptions;
  };
  industryHeader: IndustryHeader;
  audienceHeader: IndustryHeader;
  siteHeader: {
    data: {
      title?: string;
      eventId?: string;
      eventCode?: string;
      daysToEventEnd?: string;
      l1Topics?: string;
      l2Topics?: string;
      courseId?: string;
      courseCode?: string;
      daysToCourseEnd?: string;
      mainL2Topic?: string;
      googleTagManagerIds: UniqueIdentifier[];
    };
  };
  articleHome: {
    data: {
      author?: { forename?: string; surname?: string };
      communityPath?: string;
      articleId?: string;
      datePublished?: string;
      title?: string;
      relatedTopics?: any;
    };
  };
}
